(() => {
  if (window.framewerkShippingStarted) return;
  window.framewerkShippingStarted = true;
  const instances = new WeakMap();
  let quoteBusy = false;
  const STORAGE_KEY = 'framewerk_shipping_postcode';
  const COUNTRY_STORAGE_KEY = 'framewerk_shipping_country';
  const COUNTRIES = { AU: 'Australia', NZ: 'New Zealand', GB: 'United Kingdom' };
  const UK_POSTCODE = /^(?:GIR0AA|[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2})$/;
  const ROOT = () => window.Shopify?.routes?.root || '/';
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function installShippingDetails(doc) {
    const bound = new WeakSet();
    const openers = new WeakMap();
    let active = null;
    doc.addEventListener('click', event => {
      const opener = event.target.closest?.('[data-free-shipping-details]');
      if (!opener) return;
      const dialog = doc.getElementById(opener.getAttribute('aria-controls'));
      if (!dialog || typeof dialog.showModal !== 'function') return;
      event.preventDefault();
      if (active?.open && active !== dialog) active.close();
      if (!bound.has(dialog)) {
        bound.add(dialog);
        dialog.addEventListener('close', () => {
          if (active === dialog) active = null;
          const previous = openers.get(dialog);
          if (previous?.isConnected) previous.focus();
        });
        // Keep Escape local to this topmost dialog, not the underlying drawer.
        dialog.addEventListener('keydown', event => {
          if (event.key === 'Escape') event.stopPropagation();
        });
        dialog.addEventListener('cancel', event => event.stopPropagation());
        dialog.addEventListener('click', event => {
          event.stopPropagation();
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        });
      }
      openers.set(dialog, opener);
      active = dialog;
      if (!dialog.open) dialog.showModal();
    });
    for (const name of ['cart:update', 'cart:updated', 'cart:change', 'cart:refresh']) {
      doc.addEventListener(name, () => {
        if (active?.open) active.close();
      });
    }
  }

  function endpoint(path) {
    return `${ROOT()}${path}`.replace(/([^:]\/)\/+/g, '$1');
  }

  function shippingRequestError(data) {
    const fields = data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)
      ? Object.values(data.errors) : [];
    for (const candidate of [data?.description, data?.message, data?.error, data?.errors, ...fields]) {
      const messages = (Array.isArray(candidate) ? candidate : [candidate])
        .filter(value => typeof value === 'string' && value.trim() && !/[<>{}]/.test(value))
        .map(value => value.trim());
      if (messages.length) return messages.join(' ').slice(0, 500);
    }
    return 'Unable to calculate shipping right now. Please try again, or check postage at checkout.';
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {})
      },
      ...options
    });

    const text = await response.text();
    let data = null;

    try {
      data = text ? JSON.parse(text) : null;
    } catch (_) {
      data = null;
    }

    if (!response.ok) {
      throw new Error(shippingRequestError(data));
    }

    return data;
  }

  function stateForPostcode(postcode) {
    const p = Number(postcode);

    if (p >= 800 && p <= 999) return { name: 'Northern Territory', code: 'NT' };
    if ((p >= 200 && p <= 299) || (p >= 2600 && p <= 2618) || (p >= 2900 && p <= 2920)) return { name: 'Australian Capital Territory', code: 'ACT' };
    if ((p >= 1000 && p <= 2599) || (p >= 2619 && p <= 2899) || (p >= 2921 && p <= 2999)) return { name: 'New South Wales', code: 'NSW' };
    if (p >= 3000 && p <= 3999) return { name: 'Victoria', code: 'VIC' };
    if (p >= 4000 && p <= 4999) return { name: 'Queensland', code: 'QLD' };
    if (p >= 5000 && p <= 5799) return { name: 'South Australia', code: 'SA' };
    if (p >= 6000 && p <= 6797) return { name: 'Western Australia', code: 'WA' };
    if (p >= 7000 && p <= 7999) return { name: 'Tasmania', code: 'TAS' };

    return null;
  }

  function normalisePostcode(countryCode, value) {
    const compact = value.trim().toUpperCase().replace(/\s+/g, '');
    return countryCode === 'GB' && UK_POSTCODE.test(compact)
      ? `${compact.slice(0, -3)} ${compact.slice(-3)}`
      : compact;
  }

  function validPostcode(countryCode, postcode) {
    return countryCode === 'GB'
      ? UK_POSTCODE.test(postcode.replace(/\s+/g, ''))
      : (countryCode === 'AU' || countryCode === 'NZ') && /^\d{4}$/.test(postcode);
  }

  function productForm(container) {
    const section = container.closest('.product-details') || container.closest('[id^="shopify-section-"]') || document;
    return section.querySelector('form[action*="/cart/add"]');
  }

  function selectedVariantId(container) {
    const input = productForm(container)?.querySelector('[name="id"]');
    return String(input?.value || container.dataset.variantId || '');
  }

  function selectedQuantity(container) {
    const form = productForm(container);
    const input = form?.elements?.namedItem('quantity') || form?.querySelector('[name="quantity"]');
    return Number(input?.value || 1);
  }

  function cartFingerprint(cart) {
    return JSON.stringify([cart.currency, cart.total_price, cart.items?.map(item => [item.key, item.quantity])]);
  }

  function validDeliveryDate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T12:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }

  function etaDateRange(minimumDate, maximumDate) {
    if (!validDeliveryDate(minimumDate) || !validDeliveryDate(maximumDate) || maximumDate < minimumDate) {
      return 'Calculated at checkout';
    }
    const format = new Intl.DateTimeFormat('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC'
    });
    const minimum = new Date(`${minimumDate}T12:00:00Z`);
    const maximum = new Date(`${maximumDate}T12:00:00Z`);
    return minimumDate === maximumDate
      ? format.format(minimum)
      : `${format.format(minimum)} – ${format.format(maximum)}`;
  }

  // Shopify owns these fields. Never use another rate's ETA with this price.
  function shopifyArrivalLabel(rate) {
    const range = rate.delivery_range;
    if (Array.isArray(range) && range.length === 2 && range.every(validDeliveryDate) && range[1] >= range[0]) {
      return etaDateRange(range[0], range[1]);
    }
    if (validDeliveryDate(rate.delivery_date)) return etaDateRange(rate.delivery_date, rate.delivery_date);
    const days = rate.delivery_days;
    if (Array.isArray(days) && days.length && days.every(day => Number.isInteger(day) && day >= 0 && day <= 365)) {
      const minimum = Math.min(...days), maximum = Math.max(...days);
      return `${minimum === maximum ? minimum : `${minimum}–${maximum}`} ${maximum === 1 ? 'day' : 'days'} (carrier estimate)`;
    }
    return null;
  }

  function matchingConfiguredRate(rate, rates) {
    const normalise = value => typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
    const candidates = Array.isArray(rates) ? rates : [];
    const code = normalise(rate.code);
    const codeMatches = code ? candidates.filter(candidate => normalise(candidate.serviceCode) === code) : [];
    if (codeMatches.length) return codeMatches.length === 1 ? codeMatches[0] : null;
    const names = [rate.name, rate.presentment_name].map(normalise).filter(Boolean);
    const nameMatches = candidates.filter(candidate => names.includes(normalise(candidate.serviceName)));
    return nameMatches.length === 1 ? nameMatches[0] : null;
  }

  // Restore the storefront's original AU/NZ postcode estimates (0b3126eb).
  // These are the store's usual timeframes, not dates supplied by the carrier.
  // No UK baseline existed in that version, so never apply AU timings to GB.
  function destinationArrivalLabel(countryCode, postcode, now = new Date()) {
    let minimum = 4, maximum = 6;
    if (countryCode === 'NZ' && validPostcode(countryCode, postcode)) {
      [minimum, maximum] = [5, 7];
    } else if (countryCode === 'AU' && validPostcode(countryCode, postcode) && stateForPostcode(postcode)) {
      const p = Number(postcode);
      if (p >= 6000 && p <= 6199) [minimum, maximum] = [1, 2];
      else if (p >= 6200 && p <= 6797) [minimum, maximum] = [2, 4];
      else if (p >= 5000 && p <= 5199) [minimum, maximum] = [2, 4];
      else if ((p >= 3000 && p <= 3207) || (p >= 2000 && p <= 2234)) [minimum, maximum] = [3, 5];
      else if (p >= 4000 && p <= 4207) [minimum, maximum] = [4, 5];
      else if ((p >= 800 && p <= 999) || (p >= 2880 && p <= 2899) || (p >= 4450 && p <= 4499) || (p >= 4700 && p <= 4899)) [minimum, maximum] = [5, 7];
    } else return null;

    // Base store estimates on the dispatch location's calendar, not the
    // visitor's time zone. The original ranges count weekdays, not holidays.
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Perth', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(now);
    const part = type => parts.find(item => item.type === type).value;
    const start = `${part('year')}-${part('month')}-${part('day')}`;
    const afterWeekdays = count => {
      const date = new Date(`${start}T12:00:00Z`);
      for (let added = 0; added < count;) {
        date.setUTCDate(date.getUTCDate() + 1);
        if (date.getUTCDay() !== 0 && date.getUTCDay() !== 6) added++;
      }
      return date.toISOString().slice(0, 10);
    };
    return `${etaDateRange(afterWeekdays(minimum), afterWeekdays(maximum))} (store estimate)`;
  }

  function arrivalLabel(rate, configuredRate, countryCode, postcode) {
    const carrier = shopifyArrivalLabel(rate);
    if (carrier) return carrier;
    if (configuredRate) {
      const configured = etaDateRange(configuredRate.minimumDeliveryDate, configuredRate.maximumDeliveryDate);
      if (configured !== 'Calculated at checkout') return configured;
    }
    return destinationArrivalLabel(countryCode, postcode) || 'Calculated at checkout';
  }

  function handlingLabel(days) {
    if (!Number.isFinite(days)) return 'Calculated at checkout';
    if (days === 0) return 'Same business day';
    return `${days} business ${days === 1 ? 'day' : 'days'}`;
  }

  function money(value, currency = window.Shopify?.currency?.active || 'AUD') {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return String(value || '');

    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency
    }).format(amount);
  }

  function cheapestRate(rates, priceForRate) {
    return (Array.isArray(rates) ? rates : []).reduce((best, rate) => {
      const price = Number(priceForRate(rate));
      if (!Number.isFinite(price) || price < 0) return best;
      if (!best || price < best.price) return { rate, price };
      return best;
    }, null);
  }

  // The server uses an isolated Shopify session, never the shopper's cart.
  async function calculateProductQuote(variantId, quantity, countryCode, postcode) {
    const quote = await requestJSON(`${endpoint('apps/framewerk')}?resource=shipping-quote`, {
      method: 'POST',
      body: JSON.stringify({ context: 'product', variantId, quantity, countryCode, postcode })
    });
    // Refuse responses from an older server instead of silently quoting the cart.
    if (quote?.context !== 'product' || quote.variantId !== variantId || quote.quantity !== quantity
        || !/^[A-Z]{3}$/.test(quote.currencyCode || '') || !Array.isArray(quote.rates)) {
      throw new Error('Product delivery quotes are temporarily unavailable. Please try again shortly.');
    }
    return quote;
  }

  function addressParams(countryCode, postcode, state) {
    const country = COUNTRIES[countryCode];
    if (!country) throw new Error('Please select a supported delivery country.');
    const params = new URLSearchParams({
      'shipping_address[zip]': postcode,
      'shipping_address[country]': country,
      'shipping_address[country_code]': countryCode
    });

    if (countryCode === 'AU' && state) {
      params.set('shipping_address[province]', state.name);
      params.set('shipping_address[province_code]', state.code);
    }

    return params;
  }

  // Restored exactly to the original working manual-rate request flow.
  async function calculateRates(countryCode, postcode, state) {
    const params = addressParams(countryCode, postcode, state);

    await requestJSON(`${endpoint('cart/prepare_shipping_rates.json')}?${params}`, {
      method: 'POST'
    });

    for (let attempt = 0; attempt < 12; attempt++) {
      await sleep(attempt === 0 ? 400 : 700);
      const result = await requestJSON(`${endpoint('cart/async_shipping_rates.json')}?${params}`);

      if (result?.shipping_rates) return result.shipping_rates;
    }

    throw new Error('Shipping rates took too long to calculate. Please try again.');
  }

  function getRememberedPostcode(countryCode) {
    try {
      const postcode = normalisePostcode(countryCode, localStorage.getItem(STORAGE_KEY) || '');
      return validPostcode(countryCode, postcode) ? postcode : '';
    } catch (_) {
      return '';
    }
  }

  function rememberPostcode(postcode) {
    try {
      localStorage.setItem(STORAGE_KEY, postcode);
    } catch (_) {
      return;
    }
  }

  async function calculateFramewerkQuote(countryCode, postcode, totalWeightGrams) {
    return requestJSON(`${endpoint('apps/framewerk')}?resource=shipping-quote`, {
      method: 'POST',
      body: JSON.stringify({ countryCode, postcode, totalWeightGrams })
    });
  }

  function getRememberedCountry() {
    try {
      const countryCode = localStorage.getItem(COUNTRY_STORAGE_KEY) || 'AU';
      return Object.hasOwn(COUNTRIES, countryCode) ? countryCode : 'AU';
    } catch (_) {
      return 'AU';
    }
  }

  function rememberCountry(countryCode) {
    try {
      localStorage.setItem(COUNTRY_STORAGE_KEY, countryCode);
    } catch (_) {
      return;
    }
  }

  function initialise(container) {
    if (instances.has(container)) return;
    const cartContext = container.dataset.shippingContext?.startsWith('cart');

    const form = container.querySelector('[data-shipping-form]');
    const country = container.querySelector('[data-shipping-country]');
    const input = container.querySelector('[data-shipping-postcode]');
    const button = container.querySelector('[data-shipping-submit]');
    const changeButton = container.querySelector('[data-shipping-change]');
    const loading = container.querySelector('[data-shipping-loading]');
    const error = container.querySelector('[data-shipping-error]');
    const result = container.querySelector('[data-shipping-result]');
    const destination = container.querySelector('[data-shipping-destination]');
    const ratePrice = container.querySelector('[data-shipping-rate-price]');
    const service = container.querySelector('[data-shipping-service]');
    const origin = container.querySelector('[data-shipping-origin]');
    const originLabel = container.querySelector('[data-shipping-origin-label]');
    const handling = container.querySelector('[data-shipping-handling]');
    const eta = container.querySelector('[data-shipping-eta]');

    if (!country || !input || !button || !loading || !error || !result || !destination || !ratePrice || !origin || !handling || !eta) {
      console.error('Framewerk shipping calculator: required markup is missing.');
      return;
    }

    country.value = getRememberedCountry();
    const rememberedPostcode = getRememberedPostcode(country.value);
    if (rememberedPostcode) input.value = rememberedPostcode;

    const configurePostcodeInput = () => {
      const uk = country.value === 'GB';
      input.inputMode = uk ? 'text' : 'numeric';
      input.maxLength = uk ? 8 : 4;
      if (uk) input.removeAttribute('pattern');
      else input.setAttribute('pattern', '[0-9]*');
      input.setAttribute('aria-label', `${COUNTRIES[country.value]} postcode`);
    };
    configurePostcodeInput();

    let running = false;
    let revision = 0;

    const invalidate = () => {
      revision++;
      result.hidden = true;
      error.hidden = true;
      if (form) form.hidden = false;
    };
    instances.set(container, { invalidate });

    const showError = (message) => {
      error.textContent = message;
      error.hidden = false;
      result.hidden = true;
    };

    const run = async () => {
      if (running) return;
      if (quoteBusy) { showError('Another delivery quote is running. Please try again in a moment.'); return; }

      const countryCode = country.value;
      if (!Object.hasOwn(COUNTRIES, countryCode)) {
        showError('Please select a supported delivery country.');
        return;
      }
      const postcode = normalisePostcode(countryCode, input.value);
      input.value = postcode;

      if (!validPostcode(countryCode, postcode)) {
        showError(countryCode === 'GB'
          ? 'Please enter a valid UK postcode, for example SW1A 1AA.'
          : `Please enter a valid 4-digit ${COUNTRIES[countryCode]} postcode.`);
        return;
      }

      const state = countryCode === 'AU' ? stateForPostcode(postcode) : null;
      const variantId = cartContext ? null : selectedVariantId(container);
      const quantity = cartContext ? 0 : selectedQuantity(container);

      if (countryCode === 'AU' && !state) {
        showError('That postcode could not be matched to an Australian state.');
        return;
      }

      if (!cartContext && !/^[1-9]\d{0,19}$/.test(variantId)) {
        showError('Please select a product option first.');
        return;
      }
      if (!cartContext && (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 999)) {
        showError('Enter a whole quantity between 1 and 999 to estimate delivery.');
        return;
      }

      running = true;
      quoteBusy = true;
      const startedRevision = revision;
      button.disabled = true;
      country.disabled = true;
      input.disabled = true;
      loading.hidden = false;
      error.hidden = true;
      result.hidden = true;

      try {
        let rates, framewerkQuote, currency;
        if (cartContext) {
          const quotedCart = await requestJSON(endpoint('cart.js'));
          if (!quotedCart.items?.length) throw new Error('Add an item to your cart to estimate delivery.');
          if (!quotedCart.items.some(item => item.requires_shipping !== false)) {
            throw new Error('These items do not require shipping.');
          }
          const quotedFingerprint = cartFingerprint(quotedCart);
          [rates, framewerkQuote] = await Promise.all([
            calculateRates(countryCode, postcode, state),
            // Framewerk fallback rates are AUD. Never label them as another currency.
            (quotedCart.currency === 'AUD' ? calculateFramewerkQuote(countryCode, postcode, Number(quotedCart.total_weight) || 0) : Promise.resolve({ available: false, rates: [] }))
              .catch(() => ({ available: false, rates: [] }))
          ]);
          const latestCart = await requestJSON(endpoint('cart.js'));
          if (startedRevision !== revision || quotedFingerprint !== cartFingerprint(latestCart)) {
            throw new Error('Your items changed. Get a new delivery quote.');
          }
          currency = quotedCart.currency || container.dataset.currency || 'AUD';
        } else {
          const quote = await calculateProductQuote(variantId, quantity, countryCode, postcode);
          if (startedRevision !== revision || variantId !== selectedVariantId(container) || quantity !== selectedQuantity(container)) {
            throw new Error('Your items changed. Get a new delivery quote.');
          }
          rates = quote.rates;
          framewerkQuote = quote.configuredQuote || { available: false, rates: [] };
          currency = quote.currencyCode;
        }

        const shopifySelection = cheapestRate(rates, (rate) => rate.price);
        const framewerkSelection = framewerkQuote.available
          ? cheapestRate(framewerkQuote.rates, (rate) => Number(rate.priceMinor) / 100)
          : null;
        const shopifyRate = shopifySelection?.rate || null;
        const framewerkRate = framewerkSelection?.rate || null;

        if (!shopifyRate && !framewerkRate) {
          throw new Error('No shipping rates are available for this postcode.');
        }

        destination.textContent = `${COUNTRIES[countryCode]} ${postcode}`;
        if (shopifyRate) {
          // Shopify's checkout-authoritative rates always win when available.
          // Keep this amount; reuse configured timing only for an exact service match.
          const quotedService = shopifyRate.presentment_name || shopifyRate.name || shopifyRate.code || '';
          const freeStandard = cartContext && shopifySelection.price === 0 && /^standard parcel$/i.test(quotedService.trim());
          ratePrice.textContent = freeStandard
            ? 'Free standard shipping available'
            : `From ${money(shopifySelection.price, currency)}`;
          if (originLabel) originLabel.textContent = '📦 Quote covers';
          origin.textContent = cartContext ? 'Your current cart' : `This product only · quantity ${quantity}`;
          const configuredRate = framewerkQuote.available ? matchingConfiguredRate(shopifyRate, framewerkQuote.rates) : null;
          if (service) service.textContent = shopifyRate.presentment_name || shopifyRate.name || shopifyRate.code || 'Shipping service';
          handling.textContent = configuredRate ? handlingLabel(configuredRate.handlingBusinessDays) : 'Confirmed at checkout';
          eta.textContent = arrivalLabel(shopifyRate, configuredRate, countryCode, postcode);
        } else {
          ratePrice.textContent = `From ${money(framewerkSelection.price, 'AUD')}`;
          if (service) service.textContent = framewerkRate.serviceName || framewerkRate.serviceCode || 'Shipping service';
          if (originLabel) originLabel.textContent = '📍 Ships from';
          origin.textContent = framewerkRate.originLabel || 'Estimated shipping service';
          handling.textContent = handlingLabel(framewerkRate.handlingBusinessDays);
          eta.textContent = arrivalLabel({}, framewerkRate, countryCode, postcode);
        }
        rememberPostcode(postcode);
        rememberCountry(countryCode);

        if (form) form.hidden = true;
        result.hidden = false;
      } catch (err) {
        console.error('Framewerk shipping calculator:', err);
        showError(err.message || 'Unable to calculate shipping. Please try again.');
      } finally {
        loading.hidden = true;
        button.disabled = false;
        country.disabled = false;
        input.disabled = false;
        running = false;
        quoteBusy = false;
      }
    };

    input.addEventListener('input', () => {
      input.value = country.value === 'GB'
        ? input.value.toUpperCase().replace(/\s+/g, ' ').trimStart().replace(/[^A-Z0-9 ]/g, '').slice(0, 8)
        : input.value.replace(/\D/g, '').slice(0, 4);
    });

    country.addEventListener('change', () => {
      invalidate();
      configurePostcodeInput();
      if (!validPostcode(country.value, normalisePostcode(country.value, input.value))) input.value = '';
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        run();
      }
    });

    button.addEventListener('click', run);

    if (changeButton) {
      changeButton.addEventListener('click', () => {
        result.hidden = true;
        error.hidden = true;
        if (form) form.hidden = false;
        input.focus();
        input.select();
      });
    }
  }

  function boot(root = document) {
    if (root.matches?.('[data-framewerk-shipping]')) initialise(root);
    root.querySelectorAll('[data-framewerk-shipping]').forEach(initialise);
  }

  installShippingDetails(document);
  boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  // Shopify morphs cart contents after quantity changes/adds. WeakMap identity
  // (not copied data attributes) ensures replacement controls get live handlers.
  new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) if (node.nodeType === 1) boot(node);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
  for (const eventName of ['cart:update', 'cart:updated', 'cart:change', 'cart:refresh', 'variant:update']) {
    document.addEventListener(eventName, () => {
      document.querySelectorAll('[data-framewerk-shipping]').forEach(container => instances.get(container)?.invalidate());
    });
  }
  document.addEventListener('change', event => {
    if (!event.target.matches?.('[name="id"], [name="quantity"], [name="updates[]"]')) return;
    document.querySelectorAll('[data-framewerk-shipping]').forEach(container => instances.get(container)?.invalidate());
  });
})();
